"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useState } from "react";
import { createUrlWithSearchParams } from "@/shared/libs/url-utils";

export const useUrlFilters = (
  filtersToReset: string[],
  filtersToResetOnApply: { key: string; value: string | number }[],
) => {
  const pathName = usePathname();
  const params = useSearchParams();
  const { replace } = useRouter();

  const [pendingFilters, setPendingFilters] = useState<Record<string, string>>(
    {},
  );
  const [isSubmittingFilter, setIsSubmittingFilter] = useState(false);

  const resetFilters = useCallback(() => {
    const searchParams = new URLSearchParams(params.toString());
    for (const filter of filtersToReset) {
      searchParams.delete(filter);
    }
    replace(createUrlWithSearchParams(pathName, searchParams));
    setPendingFilters({});
    setIsSubmittingFilter(false);
  }, [pathName, params, replace, filtersToReset]);

  const changeFilter = useCallback(
    (key: string) => (value: string) => () => {
      setPendingFilters((prev) => {
        const newFilters = { ...prev, [key]: value };
        const hasChanges = Object.entries(newFilters).some(
          ([k, v]) => params.get(k) !== v || (!v && params.has(k)),
        );
        setIsSubmittingFilter(hasChanges);
        return newFilters;
      });
    },
    [params],
  );

  const handleApplyFilters = useCallback(() => {
    const searchParams = new URLSearchParams(params.toString());

    for (const { key, value } of filtersToResetOnApply) {
      searchParams.set(key, String(value));
    }

    Object.entries(pendingFilters).forEach(([key, value]) => {
      value ? searchParams.set(key, value) : searchParams.delete(key);
    });

    replace(createUrlWithSearchParams(pathName, searchParams));
    setPendingFilters({});
    setIsSubmittingFilter(false);
  }, [pendingFilters, pathName, params, replace, filtersToResetOnApply]);

  const hasActiveFilters = useCallback(() => {
    const urlActive = filtersToReset.some((key) => params.has(key));
    const pendingActive = Object.keys(pendingFilters).length > 0;
    return urlActive || pendingActive;
  }, [params, filtersToReset, pendingFilters]);

  const getCurrentFilterState = useCallback(
    (key: string) => pendingFilters[key] ?? params.get(key) ?? "",
    [pendingFilters, params],
  );

  return {
    isSubmittingFilter,
    resetFilters,
    changeFilter,
    handleApplyFilters,
    hasActiveFilters,
    getCurrentFilterState,
  };
};
